// Every figure shown on the Performances page is declared here, once.
//
// Source: the underlayer vs imgproxy performance report of 23 September 2026.
// It supersedes the 25 August report: five formats, three treatments each,
// fifteen comparable scenarios, plus five watermarking runs of underlayer
// alone. HEIC was not part of this run.

// One measured cell of one server: images served per second, and the 95th
// percentile of the response time in milliseconds.
export interface Measure {
	rate: number
	p95: number
}

// One scenario, the same treatment run by both servers.
export interface Scenario {
	treatment: string
	underlayer: Measure
	imgproxy: Measure
}

export interface FormatGroup {
	format: string
	scenarios: Scenario[]
}

// The treatment that leaves the image at its full size, which the
// watermarking runs below are set against.
export const PASSTHROUGH: string = 'passthrough'

export const BENCHMARK_GROUPS: FormatGroup[] = [
	{
		format: 'JPEG',
		scenarios: [
			{ treatment: PASSTHROUGH, underlayer: { rate: 296.60, p95: 268 }, imgproxy: { rate: 173.00, p95: 481 } },
			{ treatment: 'resize', underlayer: { rate: 374.80, p95: 238 }, imgproxy: { rate: 207.40, p95: 367 } },
			{ treatment: 'blur', underlayer: { rate: 138.40, p95: 530 }, imgproxy: { rate: 74.53, p95: 1037 } },
		],
	},
	{
		format: 'PNG',
		scenarios: [
			{ treatment: PASSTHROUGH, underlayer: { rate: 132.13, p95: 591 }, imgproxy: { rate: 22.27, p95: 3414 } },
			{ treatment: 'resize', underlayer: { rate: 160.40, p95: 481 }, imgproxy: { rate: 104.93, p95: 758 } },
			{ treatment: 'blur', underlayer: { rate: 94.47, p95: 800 }, imgproxy: { rate: 22.40, p95: 3377 } },
		],
	},
	{
		format: 'WebP',
		scenarios: [
			{ treatment: PASSTHROUGH, underlayer: { rate: 84.00, p95: 882 }, imgproxy: { rate: 50.67, p95: 1582 } },
			{ treatment: 'resize', underlayer: { rate: 136.07, p95: 665 }, imgproxy: { rate: 99.87, p95: 817 } },
			{ treatment: 'blur', underlayer: { rate: 77.87, p95: 931 }, imgproxy: { rate: 39.07, p95: 1923 } },
		],
	},
	{
		format: 'AVIF',
		scenarios: [
			{ treatment: PASSTHROUGH, underlayer: { rate: 19.00, p95: 4476 }, imgproxy: { rate: 17.93, p95: 4428 } },
			{ treatment: 'resize', underlayer: { rate: 35.67, p95: 2749 }, imgproxy: { rate: 26.07, p95: 2998 } },
			{ treatment: 'blur', underlayer: { rate: 26.27, p95: 2871 }, imgproxy: { rate: 18.47, p95: 4149 } },
		],
	},
	{
		format: 'BMP',
		scenarios: [
			{ treatment: PASSTHROUGH, underlayer: { rate: 184.60, p95: 610 }, imgproxy: { rate: 144.20, p95: 1026 } },
			{ treatment: 'resize', underlayer: { rate: 184.93, p95: 1499 }, imgproxy: { rate: 196.73, p95: 396 } },
			{ treatment: 'blur', underlayer: { rate: 113.13, p95: 733 }, imgproxy: { rate: 59.73, p95: 1420 } },
		],
	},
]

const SCENARIOS: Scenario[] = BENCHMARK_GROUPS.flatMap( ( group: FormatGroup ): Scenario[] => group.scenarios )

export const SCENARIO_COUNT: number = SCENARIOS.length

///@brief   underlayer's throughput over imgproxy's on one scenario.
export function speedup( scenario: Scenario ): number {
	return scenario.underlayer.rate / scenario.imgproxy.rate
}

// The headline figures are derived from the table rather than typed a second
// time: a scenario added or re-measured moves them by itself, and none of
// them can fall out of step with the rows printed underneath.

// Scenarios where underlayer serves more images per second, and where it
// answers sooner at the 95th percentile.
export const RATE_WINS: number = SCENARIOS.filter( ( s: Scenario ): boolean => speedup( s ) > 1 ).length
export const P95_WINS: number = SCENARIOS.filter( ( s: Scenario ): boolean => s.underlayer.p95 < s.imgproxy.p95 ).length

const SPEEDUPS: number[] = SCENARIOS.map( speedup ).sort( ( a: number, b: number ): number => a - b )

// An even count has no middle element, so the two straddling it are averaged.
const MEDIAN_SPEEDUP: number = SPEEDUPS.length % 2 === 1
	? SPEEDUPS[ ( SPEEDUPS.length - 1 ) / 2 ]
	: ( SPEEDUPS[ SPEEDUPS.length / 2 - 1 ] + SPEEDUPS[ SPEEDUPS.length / 2 ] ) / 2

function formatFactor( factor: number ): string {
	return `${ factor.toFixed( 1 ) }×`
}

export const SPEEDUP_MEDIAN: string = formatFactor( MEDIAN_SPEEDUP )
export const SPEEDUP_MAX: string = formatFactor( SPEEDUPS[ SPEEDUPS.length - 1 ] )

// Every scenario weighted the same: the mean throughput of each server over
// the whole table, and the ratio of the two.
function mean( values: number[] ): number {
	return values.reduce( ( sum: number, v: number ): number => sum + v, 0 ) / values.length
}

export const OVERALL_RATE_US: number = mean( SCENARIOS.map( ( s: Scenario ): number => s.underlayer.rate ) )
export const OVERALL_RATE_THEM: number = mean( SCENARIOS.map( ( s: Scenario ): number => s.imgproxy.rate ) )
export const SPEEDUP_OVERALL: string = formatFactor( OVERALL_RATE_US / OVERALL_RATE_THEM )


// The invisible watermark, run by underlayer alone: imgproxy has no
// equivalent, so these five cells have nothing to be compared against but
// underlayer's own passthrough run of the same format, which is what `cost`
// sets them against.
export interface WatermarkRun {
	format: string
	marked: Measure
	///@brief   The passthrough run of the same format, without the mark.
	bare: Measure
	///@brief   Share of the bare throughput lost to the mark, in percent;
	///         negative when the marked run came out faster, which is noise.
	cost: number
}

interface WatermarkCell {
	format: string
	marked: Measure
}

const WATERMARK_CELLS: WatermarkCell[] = [
	{ format: 'JPEG', marked: { rate: 151.27, p95: 476 } },
	{ format: 'PNG', marked: { rate: 117.53, p95: 687 } },
	{ format: 'WebP', marked: { rate: 68.93, p95: 1095 } },
	{ format: 'AVIF', marked: { rate: 19.33, p95: 3930 } },
	{ format: 'BMP', marked: { rate: 138.53, p95: 617 } },
]

function bareRun( format: string ): Measure {
	const group: FormatGroup | undefined = BENCHMARK_GROUPS.find( ( g: FormatGroup ): boolean => g.format === format )
	const scenario: Scenario | undefined = group?.scenarios.find( ( s: Scenario ): boolean => s.treatment === PASSTHROUGH )
	if ( !scenario ) throw new Error( `No ${ PASSTHROUGH } run for ${ format }` )
	return scenario.underlayer
}

export const WATERMARK_RUNS: WatermarkRun[] = WATERMARK_CELLS.map( ( cell: WatermarkCell ): WatermarkRun => {
	const bare: Measure = bareRun( cell.format )
	return {
		...cell,
		bare,
		cost: Math.round( ( 1 - cell.marked.rate / bare.rate ) * 100 ),
	}
} )

// The heaviest and lightest the mark gets, for the copy that quotes a range.
export const WATERMARK_COST_MAX: string = `${ Math.max( ...WATERMARK_RUNS.map( ( r: WatermarkRun ): number => r.cost ) ) }%`


// Every bar on the page shares one scale, the watermarking chart included,
// so a bar of one chart is directly comparable to a bar of the other instead
// of each chart being normalised to itself.
export const BENCHMARK_MAX: number = Math.max(
	...SCENARIOS.flatMap( ( s: Scenario ): number[] => [ s.imgproxy.rate, s.underlayer.rate ] ),
	...WATERMARK_RUNS.map( ( r: WatermarkRun ): number => r.marked.rate ),
)


// Methodology footprint printed under the charts. A benchmark without these
// facts is not a benchmark, it's a marketing claim.
export const BENCHMARK_RUN: string[] = [
	'Run of 23 September 2026 · imgproxy from the Docker image pinned in the report',
	'WSL2 on Linux 6.18, Intel Core i7-7820X (8C/16T), 31.2 GiB RAM',
	'underlayer release build, 16 image workers · the watermark model runs on the GPU',
	'64 virtual users per scenario, measured for 15 s after a 5 s warmup',
	'607 source images across JPEG, PNG, WebP, AVIF and BMP, 100 files drawn per format',
	'Lossy formats encoded at quality 75 by both servers',
]

// What the numbers do not say, printed with them. The run measures two
// services end to end as configured, not two codecs in isolation.
export const BENCHMARK_CAVEATS: string[] = [
	'Failure rate over every request: 0.303% for underlayer, 0.398% for imgproxy. No transport, timeout or aborted request; the unexpected responses were confined to the AVIF and BMP sources, on both servers, and are being investigated.',
	'26 of the 35 cells stayed under the run\'s 1% failure target and 9 went over it, so the run as a whole did not pass its strict per-cell reliability gate.',
	'underlayer fetched its sources over local HTTPS while imgproxy read them from a mounted filesystem, which favours imgproxy on source acquisition.',
	'The same quality setting does not guarantee identical visual quality or output size between the two servers.',
]
